import React, { memo, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  AppBar,
  Toolbar,
  Box,
  Tab,
  Tabs,
  Divider,
  Typography
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useScrollbarStyles } from 'modules/common/styles/useScrollbarStyles';
import { useSubstituteUser } from 'modules/rootPage/hooks/useSubstituteUser';
import { useMessages } from 'modules/intl';
import { useSidePanelContext } from 'modules/rootPage';
import { useFeatureFlag } from 'modules/common/hooks';
import { getParsedJwt, getTokens } from 'modules/oauth/tokenStore';
import InsightsUserPage from './InsightsUserPage';
import { TabsName } from './utils';
import SuperVisorPage from './SuperVisorPage';

type styleProps = {
  pinned: boolean;
  isTiwSupervisorInsightsEnabled: boolean;
  isSubstituteUser: boolean;
};

const useStyles = makeStyles((theme) => ({
  AppBar: {
    backgroundColor: theme.palette.common.white,
    position: 'relative',
    background: theme.palette.common.white,
    color: theme.palette.common.black
  },
  containerBox: {
    width: '100%',
    paddingLeft: ({ pinned }: styleProps) => theme.spacing(pinned ? 30 : 8)
  },
  toolbar: {
    display: 'flex',
    alignItems: 'self-end',
    paddingLeft: theme.spacing(0),
    minHeight: theme.spacing(7)
  },
  tab: {
    width: theme.spacing(25),
    color: theme.palette.text.secondary,
    fontWeight: 600
  },
  insightLabel: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.h3.fontFamily,
    fontWeight: 600
  },
  insightToolBar: {
    minHeight: theme.spacing(6.5)
  },
  contentBox: {
    height: ({ isTiwSupervisorInsightsEnabled }: styleProps) =>
      isTiwSupervisorInsightsEnabled ? 'calc(100vh - 95px)' : '100vh',
    overflowX: 'auto',
    overflowY: 'auto',
    marginTop: ({ isSubstituteUser }: styleProps) =>
      isSubstituteUser ? theme.spacing(4) : 'unset'
  }
}));
const tabLabels = [
  `insights.subMenu.${TabsName.MyInsight}`,
  `insights.subMenu.${TabsName.Supervisor}`
];
const InsightPageFC: React.FC = () => {
  const labelsMessage = useMessages(tabLabels);
  const { pinned } = useSidePanelContext();
  const [tabValue, setTabsValue] = useState<string>(TabsName.MyInsight);
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabsValue(newValue);
  };
  const isTiwSupervisorInsightsEnabled = useFeatureFlag(
    'isTiwSupervisorInsightsEnabled'
  );
  const classesScrollbar = useScrollbarStyles();
  const { isSubstituteUser } = useSubstituteUser();
  const classes = useStyles({
    pinned: pinned as boolean,
    isTiwSupervisorInsightsEnabled: isTiwSupervisorInsightsEnabled,
    isSubstituteUser: isSubstituteUser
  });

  const isSupervisorPermission = useMemo(() => {
    const token = getTokens();
    const value = getParsedJwt(token?.accessToken);
    const hasPermission = value['scope'].includes('analytics:read:supervisor');
    return hasPermission;
  }, []);

  return (
    <Box className={classes.containerBox} data-testid="insight-container">
      {isTiwSupervisorInsightsEnabled && (
        <>
          <AppBar
            className={classes.AppBar}
            elevation={0}
            data-testid="insight-AppBar"
          >
            <Toolbar className={classes.insightToolBar}>
              <Typography
                variant="h5"
                className={classes.insightLabel}
                data-testid="insight-Top-label"
              >
                <FormattedMessage id="insights.subMenu.insightLabel" />
              </Typography>
            </Toolbar>
            <Toolbar
              className={classes.toolbar}
              data-testid="insight-Tabs-toolbar"
            >
              <Tabs
                value={tabValue}
                onChange={handleChange}
                data-testid="insight-Tabs"
              >
                <Tab
                  value={TabsName.MyInsight}
                  label={
                    labelsMessage[`insights.subMenu.${TabsName.MyInsight}`]
                  }
                  className={classes.tab}
                  data-testid="insight-Tabs-myInsights"
                />
                {isSupervisorPermission && (
                  <Tab
                    value={
                      labelsMessage[`insights.subMenu.${TabsName.Supervisor}`]
                    }
                    label={TabsName.Supervisor}
                    className={classes.tab}
                    data-testid="insight-Tabs-supervisor"
                  />
                )}
              </Tabs>
            </Toolbar>
          </AppBar>
          <Divider data-testid="InsightAppBarDivider" />
        </>
      )}
      <Box
        className={classnames(classes.contentBox, classesScrollbar.scrollbar)}
      >
        {tabValue === TabsName.MyInsight ? (
          <InsightsUserPage />
        ) : (
          <SuperVisorPage />
        )}
      </Box>
    </Box>
  );
};

export default memo(InsightPageFC);
